import { useMemo, useRef, useState } from 'react';
import { CheckCircle2, FileWarning, UploadCloud } from 'lucide-react';
import EntityPicker from './EntityPicker';
import {
  commitCoverageGeometry,
  listIntakeCarriers,
  listIntakeServices,
  parseCoverageGeometry,
} from '../../../lib/coverageIntakeApi';
import { toFa } from '../../../utils/faFormat';

const MAX_FILE_BYTES = 25 * 1024 * 1024;
const ACCEPTED_EXTENSIONS = new Set(['geojson', 'json', 'kml', 'kmz']);

const PROBLEM_FA = {
  UNSUPPORTED_GEOMETRY: 'نوع هندسه پشتیبانی نمی‌شود؛ فایل مبدأ باید فقط Polygon یا MultiPolygon داشته باشد.',
  COORDINATE_OUTSIDE_IRAN: 'یک مختصات بیرون از بازهٔ منطقی ایران است؛ سامانهٔ مختصات و ترتیب طول و عرض جغرافیایی را بررسی کنید.',
  POSTGIS_VALIDATION_MISSING: 'اعتبارسنجی PostGIS برای این عارضه نتیجه نداده است؛ فایل را دوباره بررسی و بارگذاری کنید.',
  INVALID_GEOMETRY: 'هندسه معتبر نیست؛ دلیل فنی، از جمله خودتقاطع، را در فایل مبدأ اصلاح کنید.',
  AREA_TOO_SMALL: 'مساحت عارضه از حداقل مجاز کمتر است؛ واحد مختصات یا مرز ناقص را بررسی کنید.',
  AREA_TOO_LARGE: 'مساحت عارضه از حداکثر مجاز بیشتر است؛ محدوده یا سامانهٔ مختصات را بررسی کنید.',
  PUBLISHED_COVERAGE_OVERLAP: 'این محدوده با پوشش منتشرشدهٔ همان اپراتور و سرویس هم‌پوشانی دارد؛ شواهد هم‌پوشانی را بررسی کنید.',
  GEOJSON_MALFORMED: 'ساختار JSON معتبر نیست؛ فایل GeoJSON را در ابزار مبدأ اصلاح کنید.',
  NO_POLYGONS: 'فایل هیچ عارضهٔ چندضلعی ندارد؛ خروجی Polygon یا MultiPolygon تهیه کنید.',
};

function problemFa(problem) {
  return PROBLEM_FA[problem.code]
    || 'دلیل فنی بالا را در فایل مبدأ بررسی و اصلاح کنید، سپس دوباره بارگذاری کنید.';
}

function formatBytes(value) {
  const bytes = Number(value);
  if (!Number.isFinite(bytes)) return '-';
  if (bytes < 1024) return `${toFa(bytes)} بایت`;
  if (bytes < 1024 ** 2) return `${toFa(Math.round(bytes / 1024))} کیلوبایت`;
  return `${toFa((bytes / (1024 ** 2)).toFixed(1))} مگابایت`;
}

function requestId() {
  return window.crypto?.randomUUID?.()
    || '00000000-0000-4000-8000-000000000000'.replace(/[08]/g, (digit) => (
      Number(digit) ^ (window.crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (Number(digit) / 4)))
    ).toString(16));
}

function ProblemGroup({ title, severity, problems }) {
  if (!problems.length) return null;
  return (
    <section className={`coverage-intake-problems is-${severity}`}>
      <h5>{title} <span>{toFa(problems.length)}</span></h5>
      <ul aria-label={severity === 'error' ? 'خطاهای اعتبارسنجی' : 'هشدارهای اعتبارسنجی'}>
        {problems.map((problem, index) => (
          <li className="coverage-intake-problem" key={`${problem.code}-${problem.feature_ordinal ?? index}`}>
            <div className="coverage-intake-problem-head">
              <code dir="ltr">{problem.code || 'UNKNOWN_PROBLEM'}</code>
              {problem.feature_name && <strong>{problem.feature_name}</strong>}
            </div>
            <pre dir="ltr">{problem.message || problem.validity_reason || problem.code}</pre>
            <p>{problemFa(problem)}</p>
            {problem.overlaps?.length > 0 && (
              <ul className="coverage-intake-evidence" aria-label="شواهد هم‌پوشانی">
                {problem.overlaps.map((overlap, overlapIndex) => (
                  <li key={`${overlap.name}-${overlapIndex}`}>
                    {overlap.name || 'پوشش منتشرشده'}: {toFa(Math.round(Number(overlap.overlap_area_m2) || 0))} مترمربع
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

export default function GeometryIntake() {
  const fileInputRef = useRef(null);
  const [carrier, setCarrier] = useState(null);
  const [service, setService] = useState(null);
  const [regionName, setRegionName] = useState('');
  const [file, setFile] = useState(null);
  const [phase, setPhase] = useState('idle');
  const [progress, setProgress] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [auditReason, setAuditReason] = useState('');
  const [committed, setCommitted] = useState(null);

  const problems = result?.report?.problems || [];
  const errorProblems = problems.filter((problem) => (problem.severity || 'error') === 'error');
  const warningProblems = problems.filter((problem) => problem.severity === 'warning');
  const blockedReason = errorProblems.length
    ? `ثبت ممکن نیست؛ ابتدا ${toFa(errorProblems.length)} خطای مسدودکننده را در فایل مبدأ اصلاح و دوباره بارگذاری کنید.`
    : '';
  const canParse = carrier && service && file && phase === 'idle';
  const canCommit = result?.commit && !errorProblems.length && result.report?.publishable
    && auditReason.trim() && phase === 'idle';
  const context = useMemo(() => {
    if (!carrier) return 'ابتدا اپراتور و سرویس را انتخاب کنید.';
    if (!service) return `اپراتور: ${carrier.name}`;
    return `اپراتور: ${carrier.name} · سرویس: ${service.display_name || service.service_type}`;
  }, [carrier, service]);

  const loadServices = (signal) => (
    carrier ? listIntakeServices(carrier.id, signal) : Promise.resolve({ services: [] })
  );

  const pickFile = (nextFile) => {
    setResult(null);
    setCommitted(null);
    setError('');
    if (!nextFile) { setFile(null); return; }
    const extension = nextFile.name.split('.').pop()?.toLowerCase();
    if (!ACCEPTED_EXTENSIONS.has(extension)) {
      setFile(null);
      setError('فقط فایل GeoJSON، JSON، KML یا KMZ قابل پذیرش است.');
      return;
    }
    if (nextFile.size > MAX_FILE_BYTES) {
      setFile(null);
      setError('حجم فایل باید حداکثر ۲۵ مگابایت باشد.');
      return;
    }
    setFile(nextFile);
  };

  const parse = async (event) => {
    event.preventDefault();
    if (!canParse) return;
    setError('');
    setCommitted(null);
    setResult(null);
    setProgress(0);
    setPhase('uploading');
    try {
      const parsed = await parseCoverageGeometry({
        carrierId: carrier.id,
        serviceType: service.service_type,
        regionName: regionName.trim(),
        file,
        onProgress: setProgress,
        onValidation: () => setPhase('validating'),
      });
      setResult(parsed);
    } catch (parseError) {
      setError(parseError.userMessage || parseError.message || 'بارگذاری یا اعتبارسنجی فایل ناموفق بود.');
    } finally {
      setPhase('idle');
    }
  };

  const commit = async () => {
    if (!canCommit) return;
    setPhase('committing');
    setError('');
    try {
      const saved = await commitCoverageGeometry({
        commit: result.commit,
        requestId: requestId(),
        reason: auditReason.trim(),
      });
      setCommitted(saved);
      setAuditReason('');
    } catch (commitError) {
      setError(commitError.userMessage || commitError.message || 'ثبت محدوده در مرحله‌بندی ناموفق بود.');
    } finally {
      setPhase('idle');
    }
  };

  return (
    <section className="coverage-intake" aria-labelledby="coverage-intake-title">
      <div className="coverage-intake-head">
        <div>
          <h4 id="coverage-intake-title">ورود هندسهٔ پوشش</h4>
          <p>فایل را برای بررسی کامل هندسه و هم‌پوشانی بفرستید؛ ثبت نهایی فقط پس از گزارش بدون خطای مسدودکننده انجام می‌شود.</p>
        </div>
        <span>GeoJSON / KML / KMZ</span>
      </div>

      <form onSubmit={parse} className="coverage-intake-form">
        <EntityPicker
          label="اپراتور محدوده"
          value={carrier?.id || ''}
          onChange={(option) => {
            setCarrier(option);
            setService(null);
            setResult(null);
            setCommitted(null);
          }}
          loadOptions={listIntakeCarriers}
          emptyMessage="اپراتور فعالی وجود ندارد. ابتدا اپراتور را در مرجع اپراتورها ثبت کنید."
        />
        <EntityPicker
          label="سرویس محدوده"
          value={service?.id || ''}
          onChange={(option) => {
            setService(option);
            setResult(null);
            setCommitted(null);
          }}
          loadOptions={loadServices}
          deps={[carrier?.id || '']}
          disabled={!carrier}
          placeholder={carrier ? 'جست‌وجو و انتخاب سرویس' : 'ابتدا اپراتور را انتخاب کنید'}
          getOptionLabel={(option) => option.display_name || option.service_type}
          emptyMessage="این اپراتور سرویس فعالی ندارد. ابتدا سرویس را در مرجع اپراتورها تعریف کنید."
        />
        <label className="coverage-intake-region">
          <span>نام محدوده (اختیاری)</span>
          <input value={regionName} onChange={(event) => setRegionName(event.target.value)} placeholder="مثلاً تهران شمال" />
        </label>

        <div
          className={`coverage-intake-dropzone${dragging || phase === 'uploading' ? ' is-active' : ''}`}
          onDragEnter={(event) => { event.preventDefault(); setDragging(true); }}
          onDragOver={(event) => event.preventDefault()}
          onDragLeave={() => setDragging(false)}
          onDrop={(event) => {
            event.preventDefault();
            setDragging(false);
            pickFile(event.dataTransfer.files?.[0] || null);
          }}
        >
          <UploadCloud size={28} aria-hidden="true" />
          <div>
            <strong>{file ? <bdi dir="ltr">{file.name}</bdi> : 'فایل پوشش را انتخاب یا اینجا رها کنید'}</strong>
            <span>نوع‌های پذیرفته: GeoJSON، JSON، KML، KMZ · حداکثر حجم: {toFa(25)} مگابایت</span>
            <span className="coverage-intake-context">منبع جاری: {context}</span>
          </div>
          <label className="coverage-intake-file-button">
            انتخاب فایل
            <input
              ref={fileInputRef}
              type="file"
              accept=".geojson,.json,.kml,.kmz,application/geo+json,application/json,application/vnd.google-earth.kml+xml,application/vnd.google-earth.kmz"
              aria-label="فایل پوشش"
              onChange={(event) => pickFile(event.target.files?.[0] || null)}
              disabled={phase !== 'idle'}
            />
          </label>
        </div>

        {file && <div className="coverage-intake-file-meta" role="status"><bdi dir="ltr">{file.name}</bdi><span>{formatBytes(file.size)}</span></div>}
        {error && <div className="coverage-intake-alert" role="alert"><FileWarning size={17} /> <span>{error}</span></div>}
        {phase === 'uploading' && (
          <div className="coverage-intake-progress" role="status" aria-live="polite">
            <div><span style={{ width: `${progress}%` }} /></div>
            <p>ارسال فایل: {toFa(progress)}٪</p>
          </div>
        )}
        {phase === 'validating' && (
          <div className="coverage-intake-progress is-indeterminate" role="status" aria-live="polite">
            <div><span /></div><p>فایل کامل دریافت شد؛ اعتبارسنجی سمت سرور ادامه دارد…</p>
          </div>
        )}
        <button className="coverage-intake-primary" disabled={!canParse}>بارگذاری و اعتبارسنجی</button>
      </form>

      {result?.report && (
        <section className="coverage-intake-report" aria-labelledby="coverage-intake-report-title">
          <div className="coverage-intake-report-head">
            <div>
              <h4 id="coverage-intake-report-title">{toFa(problems.length)} مشکل اعتبارسنجی</h4>
              <p>{toFa(result.report.counts?.features || 0)} عارضه از <bdi dir="ltr">{result.report.file?.name}</bdi> بررسی شد.</p>
            </div>
            <span className={errorProblems.length ? 'is-blocked' : 'is-ready'}>
              {errorProblems.length ? 'نیازمند اصلاح فایل' : 'آمادهٔ مرحله‌بندی'}
            </span>
          </div>
          {!problems.length && <div className="coverage-intake-clean" role="status"><CheckCircle2 size={18} /> گزارش بدون مشکل است.</div>}
          <ProblemGroup title="خطاهای مسدودکننده" severity="error" problems={errorProblems} />
          <ProblemGroup title="هشدارهای بررسی" severity="warning" problems={warningProblems} />
          <label className="coverage-intake-audit">
            <span>دلیل ثبت در حسابرسی</span>
            <input value={auditReason} onChange={(event) => setAuditReason(event.target.value)} placeholder="مثلاً فایل رسمی اپراتور بازبینی شد" />
          </label>
          <p id="coverage-intake-commit-reason" className={blockedReason ? 'coverage-intake-blocked-reason' : 'coverage-intake-commit-hint'}>
            {blockedReason || (auditReason.trim() ? 'گزارش بدون خطاست و ثبت در مرحله‌بندی آماده است.' : 'برای ثبت، دلیل حسابرسی را وارد کنید.')}
          </p>
          <button
            type="button"
            className="coverage-intake-primary"
            disabled={!canCommit}
            aria-describedby="coverage-intake-commit-reason"
            onClick={commit}
          >
            ثبت در مرحله‌بندی
          </button>
          {committed && (
            <div className="coverage-intake-clean" role="status">
              <CheckCircle2 size={18} /> محدوده با کد <code dir="ltr">{committed.region_code}</code> در مرحله‌بندی ثبت شد.
            </div>
          )}
        </section>
      )}
    </section>
  );
}
